/**
 * Display formatting for a person's project assignments. Split from
 * `project-guidance.ts` (types + math) because the card, the detail popup and
 * the summary banner all render the same figures and must not drift apart.
 */

import { formatDateOnly } from "@/lib/dates";

/** `322.5` → `"322.5"`, `96` → `"96"`. Bare number, no unit. */
export function hoursValue(hours: number): string {
  return String(hours % 1 === 0 ? hours : hours.toFixed(1));
}

/** `322.5` → `"322.5h"`, `96` → `"96h"`. For figures that stand on their own. */
export function formatHours(hours: number): string {
  return `${hoursValue(hours)}h`;
}

/** Hours that may be absent — an em dash reads as "no data", `0h` would not. */
export function formatOptionalHours(hours: number | null | undefined): string {
  return hours === null || hours === undefined ? "—" : formatHours(hours);
}

export function formatDateRange(start: string | null, end: string | null): string | null {
  const from = formatDateOnly(start);
  const to = formatDateOnly(end);
  if (from && to) return `${from} – ${to}`;
  if (from) return `${from} – ongoing`;
  if (to) return `Until ${to}`;
  return null;
}

/**
 * `["Jan", "Feb", … "Dec"]` — the column headers of the month grid. Built once
 * from `Date` rather than hard-coded so it matches the labels elsewhere; the
 * year used is arbitrary and never shown.
 */
export const MONTH_ABBREVIATIONS = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString("en-US", { month: "short" }),
);
